import {
  Button,
  DialogBody,
  DialogHeader,
  DialogRoot,
  DialogTitle,
  HStack,
  Text,
  VStack,
} from '@chakra-ui/react';
import { useEffect, useState } from 'react';

import { CenteredDialogContent } from '#/components/ui/centered-dialog-content';
import { useAppSettings } from '#/providers/app-settings-provider';
import {
  DOCUMENT_PATH_DESCRIPTION,
  isValidDocumentPath,
} from '#/utils/document-path';

interface RenameDocumentFormProps {
  currentPath: string;
  getCurrentValue: () => string;
  onRename: (newPath: string, currentValue: string) => Promise<void>;
  onRequestClose: () => void;
  /** False when the popover or dialog is closed; resets path when opening. */
  active: boolean;
  /** Omit title lines when a dialog already provides a heading. */
  embedded?: boolean;
}

function RenameDocumentForm({
  currentPath,
  getCurrentValue,
  onRename,
  onRequestClose,
  active,
  embedded = false,
}: RenameDocumentFormProps) {
  const { documentPathRegex } = useAppSettings();
  const [renamePath, setRenamePath] = useState(currentPath);

  useEffect(() => {
    if (active) setRenamePath(currentPath);
  }, [active, currentPath]);

  const trimmedRenamePath = renamePath.trim();
  const isRenamePathValid = trimmedRenamePath
    ? isValidDocumentPath(trimmedRenamePath, documentPathRegex)
    : false;

  const handleRename = async () => {
    if (!trimmedRenamePath || !isRenamePathValid) return;
    try {
      await onRename(trimmedRenamePath, getCurrentValue());
      onRequestClose();
    } catch {
      // Parent shows toaster; keep surface open
    }
  };

  return (
    <VStack gap={3} align="stretch">
      {!embedded && (
        <>
          <Text fontSize="sm" fontWeight="semibold" color="fg">
            Rename document
          </Text>
          <Text fontSize="xs" color="fg.muted">
            Enter the new path for this document.
          </Text>
        </>
      )}
      <input
        placeholder="e.g. folder/doc-name or path (with parens)"
        value={renamePath}
        onChange={(e) => setRenamePath(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') void handleRename();
          if (e.key === 'Escape') onRequestClose();
        }}
      />
      {trimmedRenamePath && !isRenamePathValid && (
        <Text fontSize="xs" color="red.500">
          {DOCUMENT_PATH_DESCRIPTION}
        </Text>
      )}
      <HStack gap={2} justify="flex-end">
        <Button size="sm" variant="ghost" onClick={onRequestClose}>
          Cancel
        </Button>
        <Button
          size="sm"
          colorScheme="blue"
          onClick={() => void handleRename()}
          disabled={!trimmedRenamePath || !isRenamePathValid}
        >
          Rename
        </Button>
      </HStack>
    </VStack>
  );
}

export interface RenameDocumentModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  documentPathForRename: string;
  getCurrentValue: () => string;
  onRename: (newPath: string, currentValue: string) => Promise<void>;
}

export function RenameDocumentModal({
  open,
  onOpenChange,
  documentPathForRename,
  getCurrentValue,
  onRename,
}: RenameDocumentModalProps) {
  return (
    <DialogRoot open={open} onOpenChange={(e) => onOpenChange(e.open)}>
      <CenteredDialogContent>
        <DialogHeader>
          <DialogTitle>Rename document</DialogTitle>
        </DialogHeader>
        <DialogBody>
          <RenameDocumentForm
            embedded
            active={open}
            currentPath={documentPathForRename}
            getCurrentValue={getCurrentValue}
            onRename={onRename}
            onRequestClose={() => onOpenChange(false)}
          />
        </DialogBody>
      </CenteredDialogContent>
    </DialogRoot>
  );
}
